import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import MainLayout from '../../layouts/MainLayout';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus, faSlidersH, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';

const RcaList = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);
  const [rcas, setRcas] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tags, setTags] = useState('');
  const [rcaType, setRcaType] = useState('');
  const [rcaTypeHead, setRcaTypeHead] = useState('');
  const rcaTypeInitialized = useRef(false);
  const [selectedRcaId, setSelectedRcaId] = useState(null);

  const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
      rcaCode: "",
      pairCode: "",
      teamCode: "",
      taskCode: "",
      linkedRCACode: "",
      ownerCode: "",
      masterOwnerCode: "",
      rcaGroupCode: "",
    });
  
    const toggleFilters = () => setShowFilters(!showFilters);
  
    const handleFilterChange = (e) => {
      setFilters({ ...filters, [e.target.name]: e.target.value });
    };

  const fetchRcas = useCallback((rcaType) => {
    if (!rcaType) {
        return;
    }
    
    const config = {
        url: "/rca/my",
        method: "post",
        headers: { Authorization: authState.token },
        data: { rcaType: rcaType, userId: userId }
    };
    // console.log("Getting "+rcaType+" RCA",config);

    
    fetchData(config, { showSuccessToast: false })
        .then(response => {
            // console.log("Response:", response);
            if (response && Array.isArray(response.usersrca)) {
                setRcas(response.usersrca);
            } else {
                console.error("Invalid response format: usersrca not found in response");
            }
        })
        .catch(error => {
            console.error("Error fetching rcas:", error);
        });
  }, [authState.token, fetchData, rcaType, userId]);

  useEffect(() => {
      if (!authState.isLoggedIn) return;
      fetchRcas();
    }, [authState.isLoggedIn, fetchRcas]);

  useEffect(() => {
    const rcaTypeFromRoute = (location?.pathname || '').split('/').pop() || '';
    if (rcaTypeFromRoute && !rcaTypeInitialized.current) {
      let updatedRcaType = '';
      let rcaTypeHead = '';
      switch (rcaTypeFromRoute) {
        case 'my-concerns-list':
          updatedRcaType = 'Problem';
          rcaTypeHead = "Incident/Issue/Challenge/Problem";
          break;
        case 'my-objectives-list':
          updatedRcaType = 'Goal';
          rcaTypeHead = "Goal/Decision";
          break;
        default:
          updatedRcaType = 'Problem';
          rcaTypeHead = "Incident/Issue/Challenge/Problem";
          break;
      }
      setRcaType(updatedRcaType);
      setRcaTypeHead(rcaTypeHead);
      rcaTypeInitialized.current = true;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (rcaType) {
      fetchRcas(rcaType);
    }
  }, [rcaType, fetchRcas]);

    const [isDeleteClicked, setIsDeleteClicked] = useState(false);
    const deleteClicked = () => {
      setIsDeleteClicked(true);
    }
    
    const noDeleteClicked = () => {
      setIsDeleteClicked(false);
    }

    const handleDelete = (id) => {
      const config = { 
        url: `/rca/${id}`, 
        method: "delete", 
        headers: { Authorization: authState.token } 
      };
      // console.log("config for delete: ",config);
      fetchData(config).then(() => {
        setIsDeleteClicked(false);
        fetchRcas();
      });
      // fetchData(config).then(() => {
      //   setRcas(rcas.filter(rca => rca._id !== id));
      // }).catch(error => {
      //   console.error('Failed to delete the record:', error);
      // });
    }

    const handleView = (rcaId) => {
      setSelectedRcaId(prevId => (prevId === rcaId ? null : rcaId));
    };

    const handleCloseRcaDetails = () => {
      setSelectedRcaId(null);
    };

    const handleFromDateChange = (date) => {
      setFromDate(date);
    };

    const handleToDateChange = (date) => {
      setToDate(date);
    };

    const handleSearch = () => {
      // Implement search functionality
    };

    const handleRcaClick = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const handleRcaShare = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const handleRcaSave = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const handleRcaPrint = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const formatDate = (date) => {
      const jsDate = new Date(date);
      const day = jsDate.getDate().toString().padStart(2, '0');
      const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jsDate.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formatDateTime = (date) => {
      const options = {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false
      };
      const jsDate = new Date(date);
      const day = jsDate.getDate().toString().padStart(2, '0');
      const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jsDate.getFullYear();
      const hours = jsDate.getHours().toString().padStart(2, '0');
      const minutes = jsDate.getMinutes().toString().padStart(2, '0');
      const seconds = jsDate.getSeconds().toString().padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const sortedRcas = [...rcas].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  return (
    <div className='bg-black py-2 [&_p]:text-white'>
      <h2 className='flex justify-center text-lg mx-2 text-yellow-200 mb-4'>RCA Analysis - My {rcaTypeHead }List</h2>
      <div className="bg-black shadow-lg rounded-2xl p-6 mx-auto border border-blue-400">
        <h2 className="text-m font-semibold text-yellow-200 text-center mb-4">
          🔍 Advanced Search
        </h2>
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <div className="flex items-center bg-gray-100 px-3 py-2 rounded-lg">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-500 mr-2" />
            <DatePicker
              selected={fromDate}
              onChange={setFromDate}
              placeholderText="From Date"
              className="bg-transparent focus:outline-none text-sm"
            />
          </div>
          <div className="flex items-center bg-gray-100 px-3 py-2 rounded-lg">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-500 mr-2" />
            <DatePicker
              selected={toDate}
              onChange={setToDate}
              placeholderText="To Date"
              className="bg-transparent focus:outline-none text-sm"
            />
          </div>
          <input
            type="text"
            placeholder="Enter Tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="bg-gray-100 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none w-64 text-sm"
            style={{width: "400px"}}
          />
        </div>
        
        {showFilters && (
          <div className="mt-4 bg-gray-400 p-4 rounded-lg shadow-inner">
            <h3 className="text-lg font-semibold text-yellow-200 mb-3">Additional Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(filters).map((key) => (
                <input
                  key={key}
                  type="text"
                  name={key}
                  placeholder={key.replace(/([A-Z])/g, " $1").trim()}
                  value={filters[key]}
                  onChange={handleFilterChange}
                  className="bg-gray-100 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none w-full"
                />
              ))}
            </div>
          </div>
        )}
        <div className='flex flex-row justify-center'>
          <div className="flex flex-row justify-center text-center mt-4">
            <button
              onClick={toggleFilters}
              className="text-white w-60 font-semibold flex items-center justify-center gap-2 hover:text-yellow-200 text-sm"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              {showFilters ? "Hide Filters" : "More Filters"}
            </button>
          </div>
          <div className="flex justify-center mt-4">
            <button
              onClick={() => handleSearch({ fromDate, toDate, tags, ...filters })}
              className="w-60 text-sm flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg hover:text-yellow-200 transition-all"
            >
              <FontAwesomeIcon icon={faSearch} />
              Search
            </button>
          </div>
        </div>
      </div>
      <div className="border-2 border-gray-400  text-white p-4 mt-4">
        <div className="mt-2">
          {rcas && rcas.length !== 0 && (
            <div className="flex justify-between items-center">
              <div>
                <h3 className='text-xl mt-1 mx-2 text-white'>{rcas.length}{' '}{rcaTypeHead} Found!</h3>
              </div>
              <div className='mr-1'>
                <Link to='/rcas/add' className='block w-8 h-full px-2 py-1 ml-4 bg-blue-500'>
                  <FontAwesomeIcon className="bg-blue-500" icon={faPlus} />
                </Link>
              </div>
            </div>
          )}
        </div>
        {loading ? (
          <Loader />
        ) : (
          <div className='text-sm'>
            {rcas && rcas.length === 0 ? (
              <div className='w-full h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                <div className="flex justify-between items-center">
                <div>
                <h3 className='text-xl mt-1 mx-2 text-orange-400'>No Record Found!</h3>
                </div>
                <div>
                <Link to='/rcas/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                  <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Add New
                </Link>
              </div>
            </div>
              </div>
            ) : (
              sortedRcas.map((rca, index) => (
                <div key={rca._id} className='bg-black my-1 p-2 text-white border border-blue-400 rounded-md shadow-md text-sm mt-4'>
                  <div className='flex'>
                    <span className='font-medium'>#{index + 1} ({formatDateTime(rca.dateCreated)})</span>
                    <Tooltip text={"View details"} position={"top"}>
                      <span className='ml-auto mr-2 text-blue-500 cursor-pointer' onClick={() => handleView(rca._id)}>
                        <i className="fa-solid fa-eye"></i>
                      </span>
                    </Tooltip>
                    <Tooltip text={"Edit this record"} position={"top"}>
                      <Link to={`/rcas/${rca._id}`} className='mr-2 text-green-600 cursor-pointer'>
                        <i className="fa-solid fa-pen"></i>
                      </Link>
                    </Tooltip>
                    <Tooltip text={"Delete this record"} position={"top"}>
                      <span className='text-red-500 cursor-pointer mr-2' onClick={() => deleteClicked()}>
                        <i className="fa-solid fa-trash"></i>
                      </span>
                    </Tooltip>
                    { isDeleteClicked && (
                      <div>
                        <div
                          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center"
                          style={{ zIndex: 1000 }}
                        >
                          <div className="w-6/12 flex flex-row justify-center bg-blue-200 text-black border-b border-b-gray-300 mr-4 ml-4 p-4">
                              <button
                                className="bg-red-700 w-40"
                                onClick={() =>  handleDelete(rca._id) }
                                style={{ maxWidth: "150px" }}
                              >
                                Confirm To Delete
                              </button>
                              <button
                                className="bg-yellow-300 ml-4 text-black w-40"
                                onClick={() =>  noDeleteClicked() }
                                style={{ maxWidth: "150px" }}
                              >
                                No I Do Not Want To Delete
                              </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='flex flex-wrap text-white'>{rca.rcaType} : {rca.concernArea}</div>
                  {selectedRcaId === rca._id && (
                    <div className='bg-black text-white px-2 py-2 text-sm [>&_p]:text-white'>
                      <h2 className='text-yellow-400 text-xl'><strong>{rca.rcaType} Details</strong></h2>
                      <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                        <strong>5Whys Analysis</strong>
                      </p> 
                      <p><strong>Concern Area:</strong> {rca.concernArea}</p>
                      <p><strong>Problem Statement:</strong> {rca.problemStatement}</p>
                      <p><strong>Why1:</strong> {rca.why1}</p>
                      <p><strong>Why2:</strong> {rca.why2}</p>
                      <p><strong>Why3:</strong> {rca.why3}</p>
                      <p><strong>Why4:</strong> {rca.why4}</p>
                      <p><strong>Why5:</strong> {rca.why5}</p>
                      <p><strong>Root Cause:</strong> {rca.rootCause}</p>
                      <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                        <strong>Solutions & Impact</strong>
                      </p> 
                      <p><strong>Suggested Solutions:</strong> {rca.quickSolutionsNote}</p>
                      <p><strong>Selected Solution:</strong> {rca.quickSolutionsSelectionNote}</p>
                      <p><strong>Solution Actions:</strong> {rca.quickSolutionsActionsNote}</p>
                      <p><strong>Imapct Note:</strong> {rca.quickImpactNotes}</p>
                      <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                        <strong>Other Info</strong>
                      </p> 
                      <p><strong>RCA Type:</strong> {rca.rcaType}</p>
                      <p><strong>RCA Period:</strong> {rca.rcaFromDate ? formatDateTime(rca.rcaFromDate) : 'N/A'}{' to '}{rca.rcaToDate ? formatDateTime(rca.rcaToDate) : 'N/A'}</p>
                      <p><strong>Date Created:</strong> {formatDateTime(rca.dateCreated)}</p>
                      <p><strong>Your Own RCA ID:</strong> {rca.userRCAID}</p>
                      <p><strong>Think5Whys RCA Code:</strong> {rca.rcaCode}</p>
                      <p><strong>Category:</strong> {rca.category}</p>
                      <p><strong>Sub-Category:</strong> {rca.subCategory}</p>
                      <p><strong>Summary:</strong> {rca.summary}</p>
                      {/* <p><strong>Remarks:</strong> {rca.remarks}</p> */}
                      <p className='mb-4'><strong>Tags:</strong> {rca.tags}</p>
                      <p><strong>Assistant:</strong> {rca.assistant}</p>
                      <p><strong>Supervisor:</strong> {rca.supervisor}</p>
                      <p><strong>Linked RCA Code:</strong> {rca.linkedRCACode}</p>
                      <p><strong>Linked Task Code:</strong> {rca.taskCode}</p>
                      <p><strong>Public:</strong> {rca.isPublic ? "Yes" : "No"}</p>
                      <p><strong>Shared:</strong> {rca.isShared ? "Yes": "No"}</p>
                      { rca.isShared && (
                        <>
                          <p><strong>Team:</strong> {rca.teamCode}{' - '}{rca.teamName} </p>
                          <p><strong>Pair:</strong> {rca.pairCode} </p>
                        </>
                      )}
                      {rca.isSheetal && (
                        <>
                          <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>SHEETAL Info</strong>
                          </p>
                          <p><strong>Owner:</strong> {rca.ownerCode}</p>
                          <p><strong>Master Owner:</strong> {rca.masterOwnerCode}</p>
                          <p><strong>Department:</strong> {rca.masterOwnerCode}</p>                              
                          <p><strong>Sub-Department:</strong> {rca.subDepartment}</p>                              
                          <p><strong>Region:</strong> {rca.region}</p>
                          <p><strong>District:</strong> {rca.district}</p>
                          <p><strong>Sub-District/Taluka:</strong> {rca.subDistrict}</p>
                          <p><strong>Block:</strong> {rca.block}</p>
                          <p><strong>City/Village/Area:</strong> {rca.village}</p>                              
                          <p><strong>Is Priority:</strong> {rca.isPriority ? "Yes" : "No"}</p>
                          <p><strong>Reoccuring:</strong> {rca.isRecurring ? "Yes" : "No"}</p>
                          <p><strong>Completed:</strong> {rca.isCompleted ? "Yes" : "No"}</p>
                          {rca.isCompleted && (
                            <p><strong>Date Completed:</strong> {formatDateTime(rca.completionDate)}</p>
                          )}
                          <p><strong>Remarks:</strong> {rca.sheetalRemarks ? "Yes" : "No"}</p>
                          <p><strong>Final Status:</strong> {rca.status ? "Yes" : "No"}</p>
                        </>
                      )}

                      {rca.isInspire && (
                        <>
                          <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>INSPIRE Info</strong>
                          </p>
                          <p><strong>Student:</strong> {rca.student}</p>
                          <p><strong>Teacher:</strong> {rca.teacher}</p>
                          <p><strong>Principal:</strong> {rca.principal}</p>                              
                          <p><strong>Admin:</strong> {rca.admin}</p>                              
                          <p><strong>Academic Session:</strong> {rca.session}</p>
                          <p><strong>Class:</strong> {rca.studentClass}</p>
                          <p><strong>Section:</strong> {rca.section}</p>
                          <p><strong>Branch:</strong> {rca.branch}</p>
                          <p><strong>State:</strong> {rca.state}</p>  
                          <p><strong>Country:</strong> {rca.country}</p>  
                          <p><strong>Is Priority:</strong> {rca.isPriority ? "Yes" : "No"}</p>
                          <p><strong>Reoccuring:</strong> {rca.isRecurring ? "Yes" : "No"}</p>
                          <p><strong>Completed:</strong> {rca.isCompleted ? "Yes" : "No"}</p>
                          {rca.isCompleted && (
                            <p><strong>Date Completed:</strong> {formatDateTime(rca.completionDate)}</p>
                          )}
                          <p><strong>Remarks:</strong> {rca.inspireRemarks ? "Yes" : "No"}</p>
                          <p><strong>Final Status:</strong> {rca.status ? "Yes" : "No"}</p>
                        </>
                      )}
                    
                    {rca.isFinwiser && (
                        <>
                          <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>Finwiser Info</strong>
                          </p>
                          <p><strong>Employee:</strong> {rca.employee}</p>
                          <p><strong>Manager:</strong> {rca.manager}</p>
                          <p><strong>Client/Customer:</strong> {rca.customer}</p>
                          <p><strong>Partner:</strong> {rca.partner}</p>
                          <p><strong>Consultant:</strong> {rca.consultant}</p>
                          <p><strong>Branch:</strong> {rca.branch}</p>
                          <p><strong>Sub-branch:</strong> {rca.subBranch}</p>
                          <p><strong>Department:</strong> {rca.department}</p>                              
                          <p><strong>State:</strong> {rca.state}</p>                              
                          <p><strong>Country:</strong> {rca.country}</p>                            
                          <p><strong>Is Priority:</strong> {rca.isPriority ? "Yes" : "No"}</p>
                          <p><strong>Reoccuring:</strong> {rca.isRecurring ? "Yes" : "No"}</p>
                          <p><strong>Completed:</strong> {rca.isCompleted ? "Yes" : "No"}</p>
                          {rca.isCompleted && (
                            <p><strong>Date Completed:</strong> {formatDateTime(rca.completionDate)}</p>
                          )}
                          <p><strong>Remarks:</strong> {rca.finwiserRemarks ? "Yes" : "No"}</p>
                          <p><strong>Final Status:</strong> {rca.status ? "Yes" : "No"}</p>
                        </>
                      )}
                    
                    {rca.isSports && (
                        <>
                          <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>Sports Coaching FOCUS Info</strong>
                          </p>
                          <p><strong>Player:</strong> {rca.player}</p>
                          <p><strong>Coach:</strong> {rca.coach}</p>
                          <p><strong>Assistant Coach:</strong> {rca.assistantCoach}</p>                              
                          <p><strong>Ground:</strong> {rca.ground}</p>                              
                          <p><strong>Tournament:</strong> {rca.tournament}</p>
                          <p><strong>Branch:</strong> {rca.branch}</p>
                          <p><strong>State:</strong> {rca.state}</p>
                          <p><strong>Country:</strong> {rca.country}</p>
                          <p><strong>Is Priority:</strong> {rca.isPriority ? "Yes" : "No"}</p>
                          <p><strong>Reoccuring:</strong> {rca.isRecurring ? "Yes" : "No"}</p>
                          <p><strong>Completed:</strong> {rca.isCompleted ? "Yes" : "No"}</p>
                          {rca.isCompleted && (
                            <p><strong>Date Completed:</strong> {formatDateTime(rca.completionDate)}</p>
                          )}
                          <p><strong>Remarks:</strong> {rca.sportsRemarks ? "Yes" : "No"}</p>
                          <p><strong>Final Status:</strong> {rca.status ? "Yes" : "No"}</p>
                        </>
                      )}

                      {rca.isHealth && (
                        <>
                          <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>HEALTHIER Info</strong>
                          </p>
                          <p><strong>Patient:</strong> {rca.patient}</p>
                          <p><strong>Doctor:</strong> {rca.doctor}</p>
                          <p><strong>Speciality:</strong> {rca.speciality}</p>                              
                          <p><strong>Lead Doctor:</strong> {rca.headDoctor}</p>                              
                          <p><strong>Hospital:</strong> {rca.hospital}</p>
                          <p><strong>Place:</strong> {rca.village}</p>
                          <p><strong>State:</strong> {rca.state}</p>
                          <p><strong>Country:</strong> {rca.country}</p>
                          <p><strong>Is Priority:</strong> {rca.isPriority ? "Yes" : "No"}</p>
                          <p><strong>Reoccuring:</strong> {rca.isRecurring ? "Yes" : "No"}</p>
                          <p><strong>Completed:</strong> {rca.isCompleted ? "Yes" : "No"}</p>
                          {rca.isCompleted && (
                            <p><strong>Date Completed:</strong> {formatDateTime(rca.completionDate)}</p>
                          )}
                          <p><strong>Remarks:</strong> {rca.sheetalRemarks ? "Yes" : "No"}</p>
                          <p><strong>Final Status:</strong> {rca.status ? "Yes" : "No"}</p>
                        </>
                      )}

                      <Tooltip text={"Share"} position={"top"}>
                        <span className='text-green-500 cursor-pointer mr-4' disabled={true} onClick={() => handleRcaShare(rca._id)}>
                          <i className="fa-solid fa-share"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Save"} position={"top"}>
                        <span className='text-blue-500 cursor-pointer mr-4' disabled={true} onClick={() => handleRcaSave(rca._id)}>
                          <i className="fa-solid fa-save"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Print"} position={"top"}>
                        <span className='text-orange-500 cursor-pointer mr-4' disabled={true} onClick={() => handleRcaPrint(rca._id)}>
                          <i className="fa-solid fa-print"></i>
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <footer className="bg-gray-900 border-t border-blue-400 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>   
    </div>
  )
}

export default RcaList

